import React, { useState, useMemo } from 'react';
import { fmtNumber, fmtPercent, fmtDuration } from '../shared/format';
import {
  sortRows, weightedAvg,
  SortableHeader, TableSkeleton, GA4DimensionNote,
} from '../shared';
import { WindowSelector } from './WindowSelector';
import ThinScroll from '../../shared/ThinScroll';
import { ErrorState, EmptyState } from '../../shared/StateMessage';

/* ============================= Devices ============================= */
const DevicesTable = ({ data, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!searchQuery) return data || [];
    const query = searchQuery.toLowerCase();
    return (data || []).filter(row => (row.device || '').toLowerCase().includes(query));
  }, [data, searchQuery]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No device data for this period." padding="py-12" />;
  }

  const sorted = sortRows(filtered, sort.key, sort.dir);

  const totals = {
    sessions: filtered.reduce((sum, row) => sum + (row.sessions || 0), 0),
    users: filtered.reduce((sum, row) => sum + (row.users || 0), 0),
    new_users: filtered.reduce((sum, row) => sum + (row.new_users || 0), 0),
    bounce_rate: weightedAvg(filtered, 'bounce_rate', 'sessions'),
    avg_session_duration: weightedAvg(filtered, 'avg_session_duration', 'sessions'),
    pageviews: filtered.reduce((sum, row) => sum + (row.pageviews || 0), 0),
    conversions: filtered.reduce((sum, row) => sum + (row.conversions || 0), 0),
  };

  return (
    <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
      <table className="responsive-card-table w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <SortableHeader label="Device" sortKey="device" currentSort={sort} onSort={handleSort} className="min-w-[120px]" />
            <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="New Users" sortKey="new_users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Avg. Duration" sortKey="avg_session_duration" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Pageviews" sortKey="pageviews" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, index) => (
            <tr key={index} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
              <td data-label="Device" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 capitalize">{row.device || 'Unknown'}</td>
              <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
              <td data-label="New Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.new_users)}</td>
              <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.bounce_rate)}</td>
              <td data-label="Avg. Duration" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtDuration(row.avg_session_duration)}</td>
              <td data-label="Pageviews" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.pageviews)}</td>
              <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
            </tr>
          ))}
          <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
            <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({filtered.length})</td>
            <td data-label="Sessions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.sessions)}</td>
            <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.users)}</td>
            <td data-label="New Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.new_users)}</td>
            <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtPercent(totals.bounce_rate)}</td>
            <td data-label="Avg. Duration" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtDuration(totals.avg_session_duration)}</td>
            <td data-label="Pageviews" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.pageviews)}</td>
            <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.conversions)}</td>
          </tr>
        </tbody>
      </table>
    </ThinScroll>
  );
};

/* ============================= Browsers ============================= */
const BrowsersTable = ({ data, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!searchQuery) return data || [];
    const query = searchQuery.toLowerCase();
    return (data || []).filter(row => (row.browser || '').toLowerCase().includes(query));
  }, [data, searchQuery]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No browser data for this period." padding="py-12" />;
  }

  const sorted = sortRows(filtered, sort.key, sort.dir);

  const totals = {
    sessions: filtered.reduce((sum, row) => sum + (row.sessions || 0), 0),
    users: filtered.reduce((sum, row) => sum + (row.users || 0), 0),
    bounce_rate: weightedAvg(filtered, 'bounce_rate', 'sessions'),
    conversions: filtered.reduce((sum, row) => sum + (row.conversions || 0), 0),
  };

  return (
    <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
      <table className="responsive-card-table w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <SortableHeader label="Browser" sortKey="browser" currentSort={sort} onSort={handleSort} className="min-w-[160px]" />
            <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, index) => (
            <tr key={index} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
              <td data-label="Browser" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100">{row.browser || 'Unknown'}</td>
              <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
              <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.bounce_rate)}</td>
              <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
            </tr>
          ))}
          <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
            <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({filtered.length})</td>
            <td data-label="Sessions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.sessions)}</td>
            <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.users)}</td>
            <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtPercent(totals.bounce_rate)}</td>
            <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.conversions)}</td>
          </tr>
        </tbody>
      </table>
    </ThinScroll>
  );
};

/* ============================= Operating Systems ============================= */
const OperatingSystemsTable = ({ data, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!searchQuery) return data || [];
    const query = searchQuery.toLowerCase();
    return (data || []).filter(row => (row.operating_system || '').toLowerCase().includes(query));
  }, [data, searchQuery]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No OS data for this period." padding="py-12" />;
  }

  const sorted = sortRows(filtered, sort.key, sort.dir);

  const totals = {
    sessions: filtered.reduce((sum, row) => sum + (row.sessions || 0), 0),
    users: filtered.reduce((sum, row) => sum + (row.users || 0), 0),
    bounce_rate: weightedAvg(filtered, 'bounce_rate', 'sessions'),
    conversions: filtered.reduce((sum, row) => sum + (row.conversions || 0), 0),
  };

  return (
    <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
      <table className="responsive-card-table w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <SortableHeader label="Operating System" sortKey="operating_system" currentSort={sort} onSort={handleSort} className="min-w-[160px]" />
            <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, index) => (
            <tr key={index} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
              <td data-label="Operating System" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100">{row.operating_system || 'Unknown'}</td>
              <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
              <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.bounce_rate)}</td>
              <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
            </tr>
          ))}
          <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
            <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({filtered.length})</td>
            <td data-label="Sessions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.sessions)}</td>
            <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.users)}</td>
            <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtPercent(totals.bounce_rate)}</td>
            <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.conversions)}</td>
          </tr>
        </tbody>
      </table>
    </ThinScroll>
  );
};

/* ============================= Sub-tab container (re-used by Traffic Acquisition shell) ============================= */
export const TechTabContent = ({ devicesData, devicesLoading, devicesError, browsersData, browsersLoading, browsersError, browserWindows, selectedBrowserWindow, onBrowserWindowChange, osData, osLoading, osError, osWindows, selectedOsWindow, onOsWindowChange, rangeStart, rangeEnd, searchQuery = '' }) => {
  const [subTab, setSubTab] = useState('devices');

  const showBrowserWindow = subTab === 'browsers';
  const showOsWindow = subTab === 'operating_systems';

  const subTabs = [
    { id: 'devices', label: 'Devices' },
    { id: 'browsers', label: 'Browsers' },
    { id: 'operating_systems', label: 'Operating Systems' },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-1.5">
          {subTabs.map((subTabItem) => (
            <button
              key={subTabItem.id}
              type="button"
              onClick={() => setSubTab(subTabItem.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                subTab === subTabItem.id
                  ? 'bg-[#32B1FF]/10 text-[#32B1FF] border border-[#32B1FF]/30'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
              }`}
            >
              {subTabItem.label}
            </button>
          ))}
          <GA4DimensionNote className="ml-1.5" />
        </div>
        {showBrowserWindow && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">Window:</span>
            <WindowSelector
              windows={browserWindows}
              selected={selectedBrowserWindow}
              onChange={onBrowserWindowChange}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
            />
          </div>
        )}
        {showOsWindow && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">Window:</span>
            <WindowSelector
              windows={osWindows}
              selected={selectedOsWindow}
              onChange={onOsWindowChange}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
            />
          </div>
        )}
      </div>

      {subTab === 'devices' && (
        <DevicesTable data={devicesData} loading={devicesLoading} error={devicesError} searchQuery={searchQuery} />
      )}
      {subTab === 'browsers' && (
        <BrowsersTable data={browsersData} loading={browsersLoading} error={browsersError} searchQuery={searchQuery} />
      )}
      {subTab === 'operating_systems' && (
        <OperatingSystemsTable data={osData} loading={osLoading} error={osError} searchQuery={searchQuery} />
      )}
    </div>
  );
};

/* ============================= Insight summary (re-used by Traffic Acquisition shell) ============================= */
export const getTechSummary = (devData) => {
  if (!devData || devData.length === 0) return { summary: [], warnings: [] };
  const summary = [];
  const warnings = [];
  const total = devData.reduce((sum, row) => sum + (row.sessions || 0), 0);
  const mobile = devData.find(row => (row.device || '').toLowerCase() === 'mobile');
  if (mobile && total > 0) {
    const pct = ((mobile.sessions / total) * 100).toFixed(1);
    let text = `Mobile accounts for ${pct}% of sessions (${mobile.sessions.toLocaleString()})`;
    if ((mobile.bounce_rate || 0) > 0.6) {
      text += ` with a ${(mobile.bounce_rate * 100).toFixed(1)}% bounce rate`;
      warnings.push({ color: 'amber', text: `Mobile bounce rate at ${(mobile.bounce_rate * 100).toFixed(1)}% — consider optimizing mobile experience` });
    }
    text += '.';
    summary.push(text);
  }
  return { summary, warnings };
};
